if (!customElements.get('quantity-breaks')) {
  customElements.define(
    'quantity-breaks',
    class QuantityBreaks extends HTMLElement {
      connectedCallback() {
        if (window.theme?.pubsub) {
          this.init();
          return;
        }
        this._readyTimer = window.setInterval(() => {
          if (!window.theme?.pubsub) return;
          window.clearInterval(this._readyTimer);
          this.init();
        }, 50);
      }

      init() {
        if (this._initialized) return;
        this._initialized = true;
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.options = Array.from(this.querySelectorAll('[data-qb-option]'));
        this.radios = Array.from(this.querySelectorAll('input[type="radio"][name^="quantity-break-"]'));

        this.radios.forEach((radio) => {
          radio.addEventListener('change', () => this.selectTier(radio.value), { signal });
        });

        this.options.forEach((option) => {
          option.addEventListener(
            'click',
            (event) => {
              if (event.target.closest('select, button, a')) return;
              if (event.target.closest('input[type="radio"]')) return;

              const radio = option.querySelector('input[type="radio"]');
              if (!radio) return;

              // Click selected expandable again → collapse / reopen
              if (radio.checked && option.classList.contains('is-expandable')) {
                this.toggleExpanded(option);
                return;
              }

              if (radio.checked) return;
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            },
            { signal }
          );

          if (option.classList.contains('is-expandable')) {
            option.addEventListener('mouseenter', () => this.onOptionHoverEnter(option), { signal });
            option.addEventListener('mouseleave', () => this.onOptionHoverLeave(option), { signal });
            option.addEventListener('focusin', () => this.onOptionHoverEnter(option), { signal });
            option.addEventListener(
              'focusout',
              (event) => {
                if (option.contains(event.relatedTarget)) return;
                this.onOptionHoverLeave(option);
              },
              { signal }
            );
          }
        });

        this.addEventListener('change', this.onVariantSelectChange.bind(this), { signal });

        this.form?.addEventListener('submit', this.onFormSubmit.bind(this), { capture: true, signal });

        this.variantChangeUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        this.quantityInput?.addEventListener('change', this.onQuantityInputChange.bind(this), { signal });

        // Always honor configured default tier on first paint (keeps Most Popular aligned)
        const defaultTier = String(this.dataset.defaultTier || '2');
        this.selectTier(defaultTier, { silent: true });
        this.refreshPrices();
        this.updateBuyButtonPrice();
      }

      onOptionHoverEnter(option) {
        if (window.matchMedia('(hover: none)').matches) return;
        this.clearHoverOpen(option);
        option.classList.add('is-hover-open');
        this.renderVariantRowsForOption(option, Number(option.dataset.quantity || 1));
        const panel = option.querySelector('[data-qb-panel]');
        if (panel) panel.setAttribute('aria-hidden', 'false');
      }

      onOptionHoverLeave(option) {
        option.classList.remove('is-hover-open');
        const panel = option.querySelector('[data-qb-panel]');
        const keepOpen = option.classList.contains('is-expanded');
        if (panel) panel.setAttribute('aria-hidden', keepOpen ? 'false' : 'true');
      }

      clearHoverOpen(except = null) {
        this.options.forEach((option) => {
          if (except && option === except) return;
          option.classList.remove('is-hover-open');
        });
      }

      toggleExpanded(option) {
        const willExpand = !option.classList.contains('is-expanded');
        option.classList.toggle('is-expanded', willExpand);
        option.classList.remove('is-hover-open');
        const panel = option.querySelector('[data-qb-panel]');
        if (panel) panel.setAttribute('aria-hidden', willExpand ? 'false' : 'true');
        if (willExpand) {
          this.renderVariantRowsForOption(option, Number(option.dataset.quantity || 1));
        }
      }

      selectTier(tierIndex, { silent = false } = {}) {
        this.selectedTier = String(tierIndex);
        this.clearHoverOpen();
        this.options.forEach((option) => {
          const isSelected = option.dataset.tier === this.selectedTier;
          const expandable = option.classList.contains('is-expandable');
          option.classList.toggle('is-selected', isSelected);
          option.classList.toggle('is-expanded', isSelected && expandable);
          option.classList.remove('is-hover-open');
          const radio = option.querySelector('input[type="radio"]');
          if (radio) radio.checked = isSelected;

          const panel = option.querySelector('[data-qb-panel]');
          if (panel) {
            panel.setAttribute('aria-hidden', isSelected && expandable ? 'false' : 'true');
          }
        });

        const option = this.options.find((el) => el.dataset.tier === this.selectedTier);
        const qty = Number(option?.dataset.quantity || 1);
        this.syncQuantity(qty);
        this.renderVariantRows(qty);
        if (!silent) this.refreshPrices();
        this.updateBuyButtonPrice();
      }

      renderVariantRowsForOption(option, qty) {
        if (this.dataset.showVariantRows !== 'true') return;
        const container = option?.querySelector('[data-qb-variants]');
        if (!container || qty <= 1) return;

        const existingRows = container.querySelectorAll('[data-qb-variant-row]');
        if (existingRows.length === qty) return;

        const product = this.productData;
        const variant = this.currentVariant;
        if (!product || !variant) return;

        const optionNames = product.options || [];
        container.style.setProperty('--qb-option-count', String(Math.max(optionNames.length, 1)));
        container.innerHTML = `
          <p class="quantity-breaks__variants-label">${this.escapeHtml(optionNames.join(', '))}</p>
          ${Array.from({ length: qty }, (_, index) => this.variantRowMarkup(product, variant, index)).join('')}
        `;
      }

      disconnectedCallback() {
        if (this._readyTimer) window.clearInterval(this._readyTimer);
        this.abortController?.abort();
        this.variantChangeUnsubscriber?.();
      }

      get form() {
        const formId = this.getAttribute('form');
        return formId ? document.getElementById(formId) : this.closest('form');
      }

      get quantityInput() {
        const form = this.form;
        if (!form) return null;
        return (
          form.querySelector('quantity-input input[name="quantity"]') ||
          document.querySelector(`quantity-input[data-section-id="${this.dataset.sectionId}"] input[name="quantity"]`) ||
          document.querySelector(`input[name="quantity"][form="${form.id}"]`)
        );
      }

      get productData() {
        if (this._productData) return this._productData;
        const script = this.querySelector('[data-qb-product-json]');
        try {
          this._productData = script ? JSON.parse(script.textContent) : null;
        } catch (e) {
          this._productData = null;
        }
        return this._productData;
      }

      get currentVariantId() {
        const input = this.form?.querySelector('[name="id"]');
        return input ? Number(input.value) : Number(this.dataset.variantId);
      }

      get currentVariant() {
        const product = this.productData;
        if (!product) return null;
        return product.variants.find((v) => v.id === this.currentVariantId) || product.variants.find((v) => v.available) || product.variants[0];
      }

      onQuantityInputChange() {
        if (this._syncingQuantity) {
          this.updateBuyButtonPrice();
          return;
        }
        const qty = Number(this.quantityInput?.value || 1);
        const matched = this.options.find((el) => Number(el.dataset.quantity) === qty);
        if (matched && matched.dataset.tier !== this.selectedTier) {
          const radio = matched.querySelector('input[type="radio"]');
          if (radio) {
            radio.checked = true;
            this.selectTier(radio.value);
            return;
          }
        }
        this.updateBuyButtonPrice();
      }

      getSelectedTotals() {
        const variant = this.currentVariant;
        const option = this.options.find((el) => el.dataset.tier === this.selectedTier);
        const discount = Number(option?.dataset.discount || 0);
        const qty = Number(this.quantityInput?.value || option?.dataset.quantity || 1);
        if (!variant) {
          return { unit: 0, total: 0, quantity: qty, discount };
        }
        const unit = Math.round(variant.price * (1 - discount / 100));
        return { unit, total: unit * qty, quantity: qty, discount };
      }

      updateBuyButtonPrice() {
        const { total, unit } = this.getSelectedTotals();
        const form = this.form;
        if (!form || !total) return;

        const selectors = [
          `product-buy-price[form="${form.id}"]`,
          `#BuyButtonPrice-${this.dataset.sectionId}-${this.dataset.productId} product-buy-price`,
          'product-buy-price'
        ];

        let buyPrice = null;
        for (const selector of selectors) {
          buyPrice = document.querySelector(selector);
          if (buyPrice) break;
        }
        // Prefer the one tied to this form
        const formScoped = document.querySelectorAll(`product-buy-price[form="${form.id}"]`);
        if (formScoped.length) buyPrice = formScoped[0];

        if (!buyPrice) return;

        buyPrice.setAttribute('data-price', String(unit));
        if (typeof buyPrice.updatePrice === 'function') {
          buyPrice.updatePrice(total);
        } else {
          const format =
            theme?.settings?.currencyCodeEnabled || window.themeVariables?.settings?.currencyCodeEnabled
              ? theme?.settings?.moneyWithCurrencyFormat || window.themeVariables?.settings?.moneyWithCurrencyFormat
              : theme?.settings?.moneyFormat || window.themeVariables?.settings?.moneyFormat;
          buyPrice.innerHTML = theme.Currency.formatMoney(total, format);
        }

        // Sticky ATC price if present
        const stickyPrice = document.getElementById(`StickyPrice-${this.dataset.sectionId}-${this.dataset.productId}`);
        if (stickyPrice) {
          const stickyBuy = stickyPrice.querySelector('product-buy-price, .price');
          if (stickyBuy?.updatePrice) {
            stickyBuy.updatePrice(total);
          } else if (stickyBuy && stickyBuy.matches('product-buy-price')) {
            const format = theme?.settings?.moneyFormat || window.themeVariables?.settings?.moneyFormat;
            stickyBuy.innerHTML = theme.Currency.formatMoney(total, format);
          }
        }
      }

      syncQuantity(qty) {
        const input = this.quantityInput;
        if (!input) return;
        const min = Number(input.min || input.dataset.min || 1);
        const next = Math.max(qty, min);
        if (String(input.value) === String(next)) return;
        this._syncingQuantity = true;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this._syncingQuantity = false;
      }

      onVariantChange(event) {
        const detail = event?.data || event?.detail || event || {};
        if (detail.sectionId && String(detail.sectionId) !== String(this.dataset.sectionId)) return;
        if (detail.productId && String(detail.productId) !== String(this.dataset.productId)) return;
        // Keep dataset in sync with form id input
        const input = this.form?.querySelector('[name="id"]');
        if (input?.value) this.dataset.variantId = input.value;
        this.refreshPrices();
        this.syncFirstVariantRow();
        // BuyButtonPrice may be replaced by section HTML — update on next frame
        requestAnimationFrame(() => this.updateBuyButtonPrice());
      }

      onVariantSelectChange(event) {
        const select = event.target.closest('[data-qb-option-select]');
        if (!select) return;
        const row = select.closest('[data-qb-variant-row]');
        if (!row) return;
        this.updateRowFromSelects(row);
        if (row.dataset.index === '0') {
          this.pushPrimaryVariant(Number(row.dataset.variantId));
        }
      }

      refreshPrices() {
        const variant = this.currentVariant;
        if (!variant) return;

        this.options.forEach((option) => {
          const qty = Number(option.dataset.quantity || 1);
          const discount = Number(option.dataset.discount || 0);
          const unitPrice = Number(variant.price || 0);
          const unit = Math.round(unitPrice * (1 - discount / 100));
          const total = unit * qty;

          // Buy 1: show product compare-at when on sale.
          // Buy 2/3: compare against selling price × qty so % matches "get X% off".
          let compareUnit = unitPrice;
          if (discount <= 0 && variant.compare_at_price && variant.compare_at_price > unitPrice) {
            compareUnit = variant.compare_at_price;
          }
          const compareTotal = compareUnit * qty;
          const saveAmount = Math.max(compareTotal - total, 0);
          const displayPercent = discount > 0
            ? discount
            : compareTotal > 0
              ? Math.round((saveAmount / compareTotal) * 100)
              : 0;

          const priceEl = option.querySelector('[data-qb-price]');
          const compareEl = option.querySelector('[data-qb-compare]');
          const subtitleEl = option.querySelector('[data-qb-subtitle-dynamic]');
          const pillEl = option.querySelector('[data-qb-pill]');

          if (priceEl) priceEl.innerHTML = this.formatMoney(total);
          if (compareEl) {
            if (compareTotal > total) {
              compareEl.hidden = false;
              compareEl.innerHTML = this.formatMoney(compareTotal);
            } else {
              compareEl.hidden = true;
            }
          }

          if (subtitleEl) {
            const staticText = subtitleEl.dataset.static;
            if (staticText) {
              subtitleEl.textContent = staticText;
              subtitleEl.hidden = false;
            } else if (displayPercent > 0) {
              subtitleEl.textContent = `-${displayPercent}%`;
              subtitleEl.hidden = false;
            } else {
              subtitleEl.textContent = subtitleEl.dataset.fallback || 'Standard price';
              subtitleEl.hidden = !subtitleEl.textContent;
            }
          }

          if (pillEl) {
            const type = pillEl.dataset.badgeType;
            if (type === 'each' && discount > 0) {
              pillEl.hidden = false;
              pillEl.textContent = `Only ${this.stripHtml(this.formatMoney(unit))} each`;
            } else if (type === 'save' && displayPercent > 0) {
              pillEl.hidden = false;
              pillEl.textContent = `-${displayPercent}%`;
            } else if (type === 'custom') {
              pillEl.hidden = !pillEl.textContent.trim();
            } else {
              pillEl.hidden = true;
            }
          }
        });
      }

      renderVariantRows(qty) {
        if (this.dataset.showVariantRows !== 'true') return;

        const option = this.options.find((el) => el.dataset.tier === this.selectedTier);
        const container = option?.querySelector('[data-qb-variants]');
        if (!container) return;

        if (qty <= 1) {
          container.innerHTML = '';
          return;
        }

        // Already rendered for this qty — keep for smooth re-expand
        const existingRows = container.querySelectorAll('[data-qb-variant-row]');
        if (existingRows.length === qty) {
          this.syncFirstVariantRow();
          return;
        }

        const product = this.productData;
        const variant = this.currentVariant;
        if (!product || !variant) return;

        const optionNames = product.options || [];
        container.style.setProperty('--qb-option-count', String(Math.max(optionNames.length, 1)));
        container.innerHTML = `
          <p class="quantity-breaks__variants-label">${this.escapeHtml(optionNames.join(', '))}</p>
          ${Array.from({ length: qty }, (_, index) => this.variantRowMarkup(product, variant, index)).join('')}
        `;
      }

      variantRowMarkup(product, variant, index) {
        const image = this.variantImage(variant, product);
        const selects = (product.options || [])
          .map((optionName, optionIndex) => {
            const values = this.uniqueOptionValues(product, optionIndex);
            const selected = variant[`option${optionIndex + 1}`];
            return `
              <select class="quantity-breaks__select" data-qb-option-select data-option-index="${optionIndex}" aria-label="${this.escapeHtml(optionName)}">
                ${values
                  .map(
                    (value) =>
                      `<option value="${this.escapeHtml(value)}"${value === selected ? ' selected' : ''}>${this.escapeHtml(value)}</option>`
                  )
                  .join('')}
              </select>
            `;
          })
          .join('');

        return `
          <div class="quantity-breaks__variant-row" data-qb-variant-row data-index="${index}" data-variant-id="${variant.id}">
            <div class="quantity-breaks__thumb">
              ${image ? `<img src="${image}" alt="" width="80" height="80" loading="lazy">` : ''}
            </div>
            <div class="quantity-breaks__selects">${selects}</div>
          </div>
        `;
      }

      uniqueOptionValues(product, optionIndex) {
        const key = `option${optionIndex + 1}`;
        return [...new Set(product.variants.map((v) => v[key]).filter(Boolean))];
      }

      variantImage(variant, product) {
        if (variant.featured_image?.src) {
          return variant.featured_image.src.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, '_80x80$1');
        }
        const media = product.featured_image;
        if (!media) return '';
        if (typeof media === 'string') return media.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, '_80x80$1');
        return '';
      }

      updateRowFromSelects(row) {
        const product = this.productData;
        if (!product) return;
        const selects = Array.from(row.querySelectorAll('[data-qb-option-select]'));
        const options = selects
          .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
          .map((select) => select.value);
        const matched =
          product.variants.find((variant) => options.every((value, index) => variant[`option${index + 1}`] === value)) || null;
        if (!matched) return;

        row.dataset.variantId = matched.id;
        const img = row.querySelector('img');
        const src = this.variantImage(matched, product);
        if (img && src) img.src = src;
      }

      syncFirstVariantRow() {
        const option = this.options.find((el) => el.dataset.tier === this.selectedTier);
        const row = option?.querySelector('[data-qb-variant-row][data-index="0"]');
        const variant = this.currentVariant;
        if (!row || !variant || !this.productData) return;

        row.dataset.variantId = variant.id;
        row.querySelectorAll('[data-qb-option-select]').forEach((select) => {
          const index = Number(select.dataset.optionIndex);
          const value = variant[`option${index + 1}`];
          if (value) select.value = value;
        });
        const img = row.querySelector('img');
        const src = this.variantImage(variant, this.productData);
        if (img && src) img.src = src;
      }

      pushPrimaryVariant(variantId) {
        if (!variantId) return;
        const input = this.form?.querySelector('[name="id"]');
        if (!input || String(input.value) === String(variantId)) return;
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        const picker = document.querySelector(
          `variant-selects[data-section="${this.dataset.sectionId}"], variant-radios[data-section="${this.dataset.sectionId}"]`
        );
        // Soft sync: publish if theme listens
        document.dispatchEvent(
          new CustomEvent('quantity-breaks:variant', {
            bubbles: true,
            detail: { variantId, sectionId: this.dataset.sectionId, productId: this.dataset.productId }
          })
        );
      }

      getSelectedItems() {
        const option = this.options.find((el) => el.dataset.tier === this.selectedTier);
        const qty = Number(option?.dataset.quantity || 1);
        const rows = Array.from(option?.querySelectorAll('[data-qb-variant-row]') || []);

        if (rows.length >= qty) {
          const counts = new Map();
          rows.slice(0, qty).forEach((row) => {
            const id = Number(row.dataset.variantId);
            counts.set(id, (counts.get(id) || 0) + 1);
          });
          return Array.from(counts.entries()).map(([id, quantity]) => ({ id, quantity }));
        }

        return [{ id: this.currentVariantId, quantity: qty }];
      }

      onFormSubmit(event) {
        const items = this.getSelectedItems();
        const needsMulti = items.length > 1;

        // Keep native flow for single-variant quantity adds
        if (!needsMulti) {
          this.syncQuantity(items[0]?.quantity || 1);
          const idInput = this.form?.querySelector('[name="id"]');
          if (idInput && items[0]?.id) idInput.value = items[0].id;
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const form = this.form;
        const submitButton = event.submitter || form?.querySelector('[type="submit"]');
        if (submitButton?.hasAttribute('aria-disabled')) return;

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.setAttribute('aria-busy', 'true');

        const config = theme.utils.fetchConfig('javascript');
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify({
          items,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });

        fetch(theme.routes.cart_add_url, config)
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              document.dispatchEvent(
                new CustomEvent('ajaxProduct:error', {
                  detail: { errorMessage: parsedState.description || parsedState.message }
                })
              );
              return;
            }

            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson.sections = parsedState.sections;
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'quantity-breaks',
              productVariantId: items[0]?.id,
              cart: cartJson
            });
            document.dispatchEvent(
              new CustomEvent('ajaxProduct:added', {
                detail: { product: parsedState }
              })
            );
            document.querySelector('cart-drawer')?.show(submitButton);
          })
          .catch(() => {})
          .finally(() => {
            submitButton?.removeAttribute('aria-busy');
            if (!submitButton?.hasAttribute('data-sold-out')) {
              submitButton?.removeAttribute('aria-disabled');
            }
          });
      }

      formatMoney(cents) {
        const format = theme?.settings?.moneyFormat || window.themeVariables?.settings?.moneyFormat;
        if (theme?.Currency?.formatMoney) {
          return theme.Currency.formatMoney(cents, format);
        }
        return `$${(cents / 100).toFixed(2)}`;
      }

      stripHtml(html) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}
